"use client";

import {
  BellIcon,
  CirclePlusIcon,
  CreditCardIcon,
  ImageIcon,
  LogOutIcon,
  MoreVerticalIcon,
  SettingsIcon,
  UserCircleIcon,
  VideoIcon,
  WalletIcon,
} from "lucide-react";
import { Session } from "next-auth";
import { signOut } from "next-auth/react";
import Link from "next/link";
import { usePostHog } from "posthog-js/react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FrameCreditSvg } from "@/shared/svg/frame-credit-svg";

function UserAvatar({ user }: Pick<Session, "user">) {
  return (
    <Avatar className="bg h-8 w-8 rounded-full">
      <AvatarImage src={user.image ?? undefined} alt={user.email?.[0] ?? undefined} />
      <AvatarFallback className="rounded-lg">{user.email?.[0] ?? "M"}</AvatarFallback>
    </Avatar>
  );
}

const items = [
  { name: "Create Video", icon: CirclePlusIcon, link: "/create" },
  { name: "My Videos", icon: VideoIcon, link: "/dashboard/videos" },
  { name: "My Images", icon: ImageIcon, link: "/dashboard/images" },
  { name: "My Plan", icon: WalletIcon, link: "/dashboard/my-plan" },
  { name: "Settings", icon: SettingsIcon, link: "/settings" },
] as const;

export function NavigationUserAvatar({ user }: Pick<Session, "user">) {
  const posthog = usePostHog();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild className="cursor-pointer">
        <div className="flex items-center gap-1">
          <div className="flex items-center">
            <UserAvatar user={user} />
          </div>
          <div className="hidden items-center gap-1 text-center text-sm text-gray-300 lg:flex">
            <FrameCreditSvg className="h-4 w-4 stroke-gray-300" />

            <span className="hidden sm:inline-block">{user.framesCount.toLocaleString()}</span>
          </div>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
        align="end"
        sideOffset={4}
      >
        <DropdownMenuLabel className="p-0 font-normal">
          <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
            <UserAvatar user={user} />
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="flex gap-1 truncate font-medium">
                <FrameCreditSvg className="h-4 w-4 stroke-gray-300" />
                {user.framesCount.toLocaleString()}
              </span>
              <span className="text-muted-foreground truncate text-xs">{user.email}</span>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          {items.map((item) => (
            <DropdownMenuItem key={item.name} className="cursor-pointer" asChild>
              <Link href={item.link}>
                <item.icon />
                {item.name}
              </Link>
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="cursor-pointer"
          onClick={() => {
            posthog.reset();
            signOut();
          }}
        >
          <LogOutIcon />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
